import { PixelCrop } from 'react-image-crop';

import { CanvasViewType, PcodeAds, PcodeCanvasMediaSet } from '../../typings/pcode';
import {
    CreativeOptionGroupApiValue,
    CreativeOptionsConfig,
    DisclaimerCountry,
    DisclaimerGroup
} from '../CreativeOptions/typings';

import { ApiMetaDataInfo, CreativeType, ResourceType } from './typings';

export const VIDEO_ADDITIONS_URL = 'video';
export const OVERLAY_URL = 'overlay';

export type CanvasApiValidationErrors = Record<string, string[]>;

export interface CanvasApiBaseResponse {
    error?: string;
    message?: string;
    properties?: Record<string, string[]>;
    messages?: string[];
}

export interface CanvasApiItemsResponse<T> extends CanvasApiBaseResponse {
    items: T[];
    new_offset?: number;
    total: number;
}

export type UpdateQuery = {
    path: string;
    params?: string;
    options: {
        type: 'PATCH' | 'DELETE' | 'POST';
        contentType: string;
    };
};

export type UpdateType = {
    (query: UpdateQuery): Promise<void>;
};

export type CreativeCropApplyResponse = CanvasApiItemsResponse<CropApiImageItem>;

export interface CropApiImageItem extends ImageApiGalleryItem {
    cropParameters: PixelCrop;
}

export interface ApiCropData extends PixelCrop {
    id: number;
}

export interface CanvasApiBaseSuccessResponse {
    client_id: number;
    userId?: number;
    archive: boolean;
    date: string;
}

export interface ApiBaseGalleryItem extends CanvasApiBaseSuccessResponse {
    id: string;
    name?: string;
    originalName?: string;

    url: string;
}

export interface ApiBaseOverlayGallery extends CanvasApiBaseSuccessResponse {
    id: string;
    name: string;
}

interface VideoAdditionAttributes {
    stock_file_id?: string;
    sub_categories?: number[];
    conversion_task_id?: number;
    stillage_id?: number;
}

export interface VideoAdditionBaseApiGalleryItem extends ApiBaseGalleryItem, VideoAdditionAttributes {
    type: ResourceType;
}

export interface AudioApiGalleryItem extends VideoAdditionBaseApiGalleryItem {
    type: ResourceType.audio;

    duration: number;
    name: string;
}

export enum ApiItemStatus {
    ready = 'ready',
    converting = 'converting',
    new = 'new',
    error = 'error'
}

export interface VideoApiGalleryItem extends VideoAdditionBaseApiGalleryItem {
    type: ResourceType.video;
    status: ApiItemStatus;

    overlayColor: string;
    formats: Array<{ url: string }>;

    thumbnailUrl: string;
    thumbnail: {
        url: string;
        width: number;
        height: number;
        preview: {
            url: string;
            width: number;
            height: number;
        };
    };
}

export interface PackshotApiGalleryItem extends VideoAdditionBaseApiGalleryItem {
    type: ResourceType.image;
    status: string;

    thumbnailUrl: string;
    previewUrl: string;
    largePreviewUrl: string;

    formats: Array<{
        url: string;
        height: number;
        width: number;
        path: string;
        size: string;
    }>;
}

export type VideoAdditionApiGalleryItem = AudioApiGalleryItem | PackshotApiGalleryItem | VideoApiGalleryItem;

export interface ImageColors {
    averageBorderColor: string;
    colorWizBack: string;
    colorWizButton: string;
    colorWizButtonText: string;
    colorWizText: string;
}

export interface StillageFileInfo {
    metadataInfo: ApiMetaDataInfo;
    contentGroup?: string;
    fileSize?: number;
    id?: string;
    imageColors?: ImageColors;
    md5Hash?: string;
    mimeType?: MimeType;
    url: string;
}

export interface ApiOverlayGallery extends ApiBaseOverlayGallery {
    type: ResourceType.overlay;
}

export interface ImageApiGalleryItem extends ApiBaseGalleryItem {
    type: undefined;

    stockFileId?: string;
    stillageFileId?: string;
    stillageFileInfo: StillageFileInfo;
    originalFileId?: string;

    cropSourceUrl: string;
    cropParameters?: PixelCrop;

    thumbnailUrl: string;
    previewUrl: string;
    largePreviewUrl: string;

    categories: number[];
}

export type ApiGalleryItem = VideoAdditionApiGalleryItem | ImageApiGalleryItem;

export interface VideoPreviewApiValue {
    bundle: {
        name: string;
    };
    elements: CreativeOptionGroupApiValue[];
    width?: number;
    height?: number;
}

interface VideoAdditionBaseApiResponse {
    id: string;
    name: string;
    config: CreativeOptionsConfig;
    data: VideoPreviewApiValue;
    preset_id?: number;
}

export interface VideoBasedOnAdditionApiResponse
    extends VideoAdditionBaseApiResponse,
        CanvasApiBaseSuccessResponse,
        CanvasApiBaseResponse {
    preset_id: number;
    screenshot_url: string;
    creative_id: number;
    stock_creative_id: number;
    previewUrl: string;
    has_packshot: boolean;
    vast: string;
}

export interface VideoFromCreativeIdApiResponse extends CanvasApiBaseSuccessResponse, CanvasApiBaseResponse {
    id: string;
    name: string;
    data: VideoPreviewApiValue;
    preset_id: number;
    screenshot_url: string;
    creative_id: number;
    stock_creative_id: number;
    previewUrl: string;
    has_packshot: boolean;
    vast: string;
}

export interface VideoAdditionPresetApiResponse extends VideoAdditionBaseApiResponse, CanvasApiBaseResponse {
    preset_id: undefined;
    packshot_present: boolean;
    skip_offset: number;
    tags: CreativeType[];
    thumbnail: string;
    allow_stock_video: boolean;
}

export type VideoAdditionApiResponse = VideoAdditionPresetApiResponse | VideoBasedOnAdditionApiResponse;
export type FetchVideoDataResponse = VideoAdditionApiGalleryItem & CanvasApiBaseResponse & { error_message?: string };
export type FetchPreviewVastResponse = { vast: string } & CanvasApiBaseResponse;

export interface FetchPreviewVastRequestData {
    data: VideoPreviewApiValue;
    preset_id: string;
    previewData: PcodeAds;
}

export interface FetchPreviewOverlayVastRequestData {
    data: VideoPreviewApiValue;
    preset_id: string;
}

export const EMPTY_VIDEO_PREVIEW_DATA = {
    domain: '',
    punyDomain: '',
    url: '',
    warning: ''
};

export interface CreateVideoAdditionRequestData {
    data: VideoPreviewApiValue;
    preset_id: string;
    name: string;
}

export interface CreateOverlayRequestData {
    data: VideoPreviewApiValue;
    preset_id: string;
    name: string;
}

export interface CreateVideoAdditionApiResponse extends CanvasApiBaseResponse, CanvasApiBaseSuccessResponse {
    id: string;
    preset_id: string;
    name: string;
    data: VideoPreviewApiValue;

    screenshot_url: string;
    stock_creative_id: number;
    previewUrl: string;
    vast: string;
    creative_id: number;
}

export type MediaSets = {
    [key: string]: PcodeCanvasMediaSet;
};

export type Bundle = { name: CanvasViewType; version?: number };

export interface CommonCreativeOptions {
    backgroundColor?: string;
    borderColor?: string;
    hasAnimation?: boolean;
    isAdaptive: boolean;
    minWidth?: number;
    minHeight?: number;
}

export interface CommonCreativeBaseApiItem {
    width: number;
    height: number;
    bundle: Bundle;
    elements: CreativeOptionGroupApiValue[];
    // у всех типов баннеров разные, задает картинки, включая croppedFileId и размеры кропа
    mediaSets: MediaSets;
    // у всех типов баннеров одинаковые, задает опции рамки/фона
    options: CommonCreativeOptions;
}

export interface CommonCreativePresetApiItem extends CommonCreativeBaseApiItem {
    id: number;
}

export interface CommonCreativeApiItem {
    id: number;
    creativeURL: string;
    available: boolean;
    name: string;

    data: CommonCreativeBaseApiItem;

    date: string;
    batchId: string;
    screenshotURL: string;
    previewURL: string;
    screenshotIsDone: boolean;
    presetId?: number;
}

export interface CommonCreativeIdeaApiItem {
    id: number;
    data: CommonCreativeBaseApiItem;
    presetId: number;
}

export interface CommonCreativePresetApiResponse extends CanvasApiBaseResponse {
    id: string | number;
    name: string;
    tags: string[];
    thumbnail: string;
    items: CommonCreativePresetApiItem[];
}

export interface CommonCreativeIdeaApiResponse extends CanvasApiBaseResponse {
    ideaId: string;
    name: string;
    url: string;
    items: CommonCreativeIdeaApiItem[];
}

export interface CommonCreativeApiResponse extends CanvasApiBaseResponse {
    clientId: number;
    id: string;
    name: string;
    date: string;
    total: number;
    items: CommonCreativeApiItem[];
}

export interface DisclaimersApiResponse extends CanvasApiBaseResponse {
    groups: DisclaimerGroup[];
    items: DisclaimerCountry[];
}

export interface Html5CreativesApiResponse extends CanvasApiBaseResponse {
    items: Html5CreativeItem[];
    total: number;
    new_offset: number;
}

export type Html5CreativeItem = {
    id: string;
    name: string;
    archive: boolean;
    available: boolean;
    creatives: Html5Creative[];
};

type Html5Creative = {
    id: number;
    width: number;
    height: number;
    available: boolean;
    preview_url: string;
    screenshot_url: string;
    archive_url: string;
    base_path: string;
};

export interface UploadHtml5Response extends CanvasApiBaseResponse {
    id: string;
    name: string;
    width: number;
    height: number;
    url: string;
    screenshot_url: string;
    preview_url: string;
    stillage_info: {
        id: string;
        url: string;
        fileSize: number;
    };
    base_path: string;
}

export interface CreateHtml5Response extends CanvasApiBaseResponse, Html5CreativeItem {}

export type FetchCommonFileDataResponse = ImageApiGalleryItem & CanvasApiBaseResponse & { error_message?: string };
export type CropFileApiResponse = CanvasApiBaseResponse & CanvasApiItemsResponse<ImageApiGalleryItem>;

export interface CreateCommonCreativeRequestData {
    name: string;
    path: string;
    items: {
        available: boolean;
        presetId?: string;
        batchId?: string;
        data: CommonCreativeBaseApiItem;
    }[];
    ideaId?: string;
}

export type CreateCommonApiResponse = CanvasApiBaseResponse &
    CanvasApiItemsResponse<CommonCreativeApiItem> & {
        clientId: number;
        date: string;
        id: number;
        name: string;
    };
